// Exam controls: Submit, Save & Next, Mark for Review, End Exam
import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Stack,
  Chip
} from '@mui/material';
import {
  Send as SubmitIcon,
  ExitToApp as EndIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useExam } from './ExamContext';

const ExamControls = () => {
  const { state, dispatch } = useExam();
  const { questions, answers, status } = state;
  const [showSubmit, setShowSubmit] = useState(false);
  const [showEnd, setShowEnd] = useState(false);

  // Check if all questions are answered
  const allAnswered = questions.every(q => {
    const a = answers[q.id];
    return a && a.answer !== undefined && a.answer !== '';
  });

  const answeredCount = questions.filter(q => {
    const a = answers[q.id];
    return a && a.answer !== undefined && a.answer !== '';
  }).length;

  const handleSubmit = () => {
    setShowSubmit(false);
    dispatch({ type: 'SET_STATUS', payload: 'submitted' });
    // TODO: Add actual submit logic (API call, result page, etc.)
  };
  
  const handleEndExam = () => {
    setShowEnd(false);
    dispatch({ type: 'SET_STATUS', payload: 'submitted' });
    // TODO: Add end exam logic
  };

  const handleShowSubmit = () => {
    setShowSubmit(true);
  };

  return (
    <Box 
      sx={{
        position: 'sticky',
        bottom: 0,
        background: 'linear-gradient(to top, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
        backdropFilter: 'blur(8px)',
        borderTop: '1px solid',
        borderColor: 'divider',
        p: 3,
        zIndex: 100
      }}
    >
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        justifyContent="center"
        alignItems="center"
      >
        <Chip 
          label={`${answeredCount}/${questions.length} Answered`}
          color={allAnswered ? 'success' : 'warning'}
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
        
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={handleShowSubmit}
            startIcon={<SubmitIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              boxShadow: 3
            }}
          >
            Submit Exam
          </Button>
          
          <Button
            variant="outlined"
            color="error"
            size="large"
            onClick={() => setShowEnd(true)}
            startIcon={<EndIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              px: 4,
              py: 1.5,
              textTransform: 'none'
            }}
          >
            End Exam
          </Button>
        </Stack>
      </Stack>

      {/* Submit Confirmation Dialog */}
      <Dialog 
        open={showSubmit} 
        onClose={() => setShowSubmit(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: '1.5rem' }}>
          Submit Exam?
        </DialogTitle>
        <DialogContent>
          {!allAnswered && (
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" fontWeight={600}>
                You have {questions.length - answeredCount} unanswered questions. 
                You can still submit, but review your answers if needed.
              </Typography>
            </Alert>
          )}
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Are you sure you want to submit your answers? 
            You cannot change them after submission.
          </Typography>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Chip 
              label={`${answeredCount}/${questions.length} Questions Answered`}
              color={allAnswered ? 'success' : 'warning'}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            color="success"
            size="large"
            sx={{ 
              borderRadius: 2, 
              fontWeight: 700, 
              px: 4,
              textTransform: 'none'
            }}
          >
            Yes, Submit
          </Button>
          <Button 
            onClick={() => setShowSubmit(false)}
            variant="outlined"
            size="large"
            sx={{ 
              borderRadius: 2, 
              fontWeight: 700, 
              px: 4,
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* End Exam Confirmation Dialog */}
      <Dialog 
        open={showEnd} 
        onClose={() => setShowEnd(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: '1.5rem' }}>
          End Exam?
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Warning: Ending the exam will terminate your session.
            </Typography>
          </Alert>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Are you sure you want to end the exam? All progress will be saved.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={handleEndExam}
            variant="contained"
            color="error"
            size="large"
            sx={{ 
              borderRadius: 2, 
              fontWeight: 700, 
              px: 4,
              textTransform: 'none'
            }}
          >
            Yes, End Exam
          </Button>
          <Button 
            onClick={() => setShowEnd(false)}
            variant="outlined"
            size="large"
            sx={{ 
              borderRadius: 2, 
              fontWeight: 700, 
              px: 4,
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExamControls;
